import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { isDesktop } from 'helpers/ClientUtils';
import timedFetch from 'middleware/timedFetch';
import { trackError } from 'helpers/ErrorUtils';
import { getMicrosoftSponsoredAds, RequestMicrosoftAdsBody, RequestMicrosoftAdsBodyFilters, RequestMicrosoftAdsResponse } from 'apis/sponsoredAds';

export const trackMicrosoftAdImpressions = ({ url }: { url: string }, fetcher = timedFetch) => {
  const fetch = fetcher('MicrosoftAdImpressions');
  fetch(url).catch((error: unknown) => trackError('ERROR', 'Failed to send MSFT impression events', error));
};

function concatIfExists(keyword = '', val?: any) {
  if (val) {
    //purposeful whitespace.
    return `${keyword} ${val}`;
  }
  return keyword;
}

// builds the keyword for search implementation based off filters.
// keyword should be "gender + most specific zc + brand[0] + color[0]"
export function buildKeywordFromFilters(filters: Partial<RequestMicrosoftAdsBodyFilters>) {
  //find most specific zc- 'zc1', 'zc2', etc.
  const mostSpecificZcKey = Object.keys(filters).reduce((val, current) => {
    if (current.includes('zc')) {
      //ex: zc2 > zc1
      return current > val ? current : val;
    }

    return val;
  }, '') as keyof RequestMicrosoftAdsBodyFilters;

  //all of these are optional on the filters object, if they exist add it to the keyword.
  let keyword = filters.txAttrFacet_Gender?.[0] || '';
  keyword = concatIfExists(keyword, filters[mostSpecificZcKey]?.[0]);
  keyword = concatIfExists(keyword, filters.brandNameFacet?.[0]);
  keyword = concatIfExists(keyword, filters.colorFacet?.[0]);
  return keyword;
}

//configure the payload for the get request to sponsoredAds endpoint
//handle the response and return the results.
export function fetchSponsoredAds(
  args: Omit<RequestMicrosoftAdsBody, 'isMobile'>,
  trackAdImpression = trackMicrosoftAdImpressions
): Promise<RequestMicrosoftAdsResponse> {
  const body = {
    isMobile: !isDesktop(),
    ...args
  };

  return getMicrosoftSponsoredAds(body).then((results: RequestMicrosoftAdsResponse) => {
    // we should always fire this whenever we receive an impressionUrl.
    if (results?.impressionUrl) {
      trackAdImpression({ url: results.impressionUrl });
    }
    return results;
  });
}

type UseSponsoredAds = Pick<RequestMicrosoftAdsBody, 'pageType'> & Partial<Pick<RequestMicrosoftAdsBody, 'keywords' | 'minRes' | 'maxRes'>>;

//usage in PDP
type UseSponsoredAdsWithStockId = UseSponsoredAds & Required<Pick<RequestMicrosoftAdsBody, 'stockId' | 'keywords'>>;

//usage in Search
type UseSponsoredAdsWithFilters = UseSponsoredAds &
  Required<Pick<RequestMicrosoftAdsBody, 'filters'>> &
  Pick<RequestMicrosoftAdsBody, 'placementId' | 'term'>;

function isUseSponsoredAdsWithFilters(args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters): args is UseSponsoredAdsWithFilters {
  return (args as UseSponsoredAdsWithFilters).filters !== undefined;
}

export const useSponsoredAds = (args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters, fetchAds = fetchSponsoredAds) => {
  const [sponsoredAds, setSponsoredAds] = useState<RequestMicrosoftAdsResponse>();
  const sessionId = useSelector(state => state.cookies?.['session-id']);
  const { title: titleTag, canonical: url } = useSelector(state => state.meta?.documentMeta ?? {}); //documentMeta can be null

  // require all pieces of information before fetching sponsoredAds.
  useEffect(() => {
    // required pieces from state that are async changes via other actions.
    if (!sessionId || !args.pageType || !titleTag || !url) return;

    // we need either or of these so if they dont exist, return;
    if (!(args as UseSponsoredAdsWithStockId).stockId && !(args as UseSponsoredAdsWithFilters).filters) return;

    const payload = {
      sessionId,
      titleTag,
      url,
      minRes: 5,
      maxRes: 15,
      ...args
    } as RequestMicrosoftAdsBody;

    if (isUseSponsoredAdsWithFilters(args)) {
      payload.keywords = [buildKeywordFromFilters(args.filters)];
    }

    fetchAds(payload).then(setSponsoredAds);
  }, [sessionId, titleTag, url, JSON.stringify(args)]);

  return sponsoredAds;
};
